import React, { useState, useRef, useCallback } from 'react';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import {
  Receipt,
  SpinnerGap,
  X,
  Check,
  Warning,
  Camera,
  Plus,
  CaretDown,
  CaretUp,
  MapPin
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

const ScanBillModal = ({ open, onOpenChange, onVisitCreated, restaurants = [] }) => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | scanning | review | saving
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [showItems, setShowItems] = useState(false);

  // Editable fields (after scan)
  const [restaurantName, setRestaurantName] = useState('');
  const [billDate, setBillDate] = useState('');
  const [totalBill, setTotalBill] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [matchedRestaurant, setMatchedRestaurant] = useState(null);
  const [createNewRestaurant, setCreateNewRestaurant] = useState(false);

  const resetModal = () => {
    setStep('upload');
    setImageFile(null);
    setImagePreview(null);
    setScanResult(null);
    setShowItems(false);
    setRestaurantName('');
    setBillDate('');
    setTotalBill('');
    setTipAmount('');
    setMatchedRestaurant(null);
    setCreateNewRestaurant(false);
  };

  const handleClose = () => {
    resetModal();
    onOpenChange(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WEBP image');
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!imageFile) return;

    setStep('scanning');
    try {
      const formData = new FormData();
      formData.append('file', imageFile);

      const response = await api.post('/bills/scan', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      const data = response.data;
      setScanResult(data);

      // Pre-fill editable fields
      setRestaurantName(data.restaurant_name || '');
      setBillDate(data.bill_date || new Date().toISOString().split('T')[0]);
      setTotalBill(data.total_bill?.toString() || '');
      setTipAmount(data.tip?.toString() || '');

      // Check for matched restaurant
      if (data.matched_restaurant) {
        setMatchedRestaurant(data.matched_restaurant);
      }

      setStep('review');
      toast.success('Bill scanned successfully!');
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan bill. Please try again.');
      setStep('upload');
    }
  };

  const handleSaveVisit = async () => {
    setStep('saving');

    try {
      let restaurantId = matchedRestaurant?.id;

      // If no match or user wants to create new
      if (!restaurantId || createNewRestaurant) {
        const newRestaurant = await api.post('/restaurants', {
          name: restaurantName.trim(),
          state: 'visited',
        });
        restaurantId = newRestaurant.data.id;
        toast.success(`Created "${restaurantName}" and logging visit`);
      }

      // Create visit
      const visitData = {
        visited_at: new Date(billDate).toISOString(),
        meal_type: 'dinner',
        overall_rating: 0,
        bill_total: totalBill ? parseFloat(totalBill) : null,
        tip_amount: tipAmount ? parseFloat(tipAmount) : null,
        dishes_ordered: scanResult?.items?.map(i => i.name).filter(Boolean) || [],
        notes: `Bill scanned via OCR. ${scanResult?.items?.length || 0} items extracted.`,
        bill_extraction: {
          status: 'completed',
          restaurant_name: restaurantName,
          bill_date: billDate,
          total_bill: totalBill ? parseFloat(totalBill) : null,
          tip: tipAmount ? parseFloat(tipAmount) : null,
          items: scanResult?.items || [],
          confidence: scanResult?.confidence || 0,
        },
      };

      const visitResp = await api.post(`/restaurants/${restaurantId}/visits`, visitData);

      // Upload bill image as attachment
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('attachment_type', 'bill_photo');
        await api.post(`/visits/${visitResp.data.id}/attachments?attachment_type=bill_photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success('Visit logged from bill!');
      if (onVisitCreated) onVisitCreated(visitResp.data);
      handleClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save visit');
      setStep('review');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="scan-bill-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Receipt weight="duotone" className="w-7 h-7 text-[#A94438]" />
            Scan Bill
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />

            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Bill preview"
                  className="w-full max-h-[300px] object-contain rounded-xl border border-[#EAE8E3]"
                />
                <button
                  onClick={() => { setImageFile(null); setImagePreview(null); }}
                  className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow"
                  data-testid="remove-bill-image"
                >
                  <X weight="bold" className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="dropzone w-full py-12"
                data-testid="bill-upload-area"
              >
                <Camera weight="duotone" className="w-12 h-12 text-[#A94438] mx-auto mb-3" />
                <p className="text-base font-medium text-[#5A5752]">Upload a bill photo</p>
                <p className="text-sm text-[#9A9590] mt-1">JPEG, PNG or WEBP</p>
              </button>
            )}

            <Button
              onClick={handleScan}
              disabled={!imageFile}
              className="w-full btn-primary"
              data-testid="scan-bill-btn"
            >
              <Receipt weight="bold" className="w-5 h-5 mr-2" />
              Scan & Extract
            </Button>
          </div>
        )}

        {/* Step 2: Scanning */}
        {step === 'scanning' && (
          <div className="flex flex-col items-center py-12 space-y-4">
            <div className="relative">
              <img
                src={imagePreview}
                alt="Scanning..."
                className="w-48 h-48 object-cover rounded-xl opacity-60"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <SpinnerGap className="w-12 h-12 text-[#A94438] animate-spin" />
              </div>
            </div>
            <p className="text-lg font-heading font-semibold text-[#1A1918]">Scanning your bill...</p>
            <p className="text-sm text-[#7A7873]">Extracting restaurant, total, and line items</p>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && scanResult && (
          <div className="space-y-4">
            {/* Confidence indicator */}
            <div className="flex items-center gap-2 text-sm">
              {scanResult.confidence >= 0.7 ? (
                <Badge className="bg-[#4B5945] text-white">High confidence</Badge>
              ) : scanResult.confidence >= 0.4 ? (
                <Badge className="bg-[#C4A35A] text-white">Medium confidence</Badge>
              ) : (
                <Badge className="bg-[#A94438] text-white">Low confidence — please review</Badge>
              )}
              <span className="text-[#9A9590]">{Math.round((scanResult.confidence || 0) * 100)}%</span>
            </div>

            {/* Bill image thumbnail */}
            <div className="h-24 rounded-lg overflow-hidden border border-[#EAE8E3]">
              <img src={imagePreview} alt="Bill" className="w-full h-full object-cover" />
            </div>

            {/* Restaurant match */}
            {matchedRestaurant && !createNewRestaurant ? (
              <div className="bg-[#F0EDE8] rounded-lg p-3 border border-[#E8DCD0]" data-testid="restaurant-match">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-[#4B5945]">
                      <Check weight="fill" className="w-4 h-4" />
                      <span className="font-medium">Matched: {matchedRestaurant.name}</span>
                    </div>
                    {matchedRestaurant.suburb && (
                      <div className="flex items-center gap-1 text-xs text-[#7A7873] ml-6">
                        <MapPin weight="duotone" className="w-3 h-3" />
                        {matchedRestaurant.suburb}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setCreateNewRestaurant(true)}
                    className="text-xs text-[#A94438] underline"
                    data-testid="use-different-restaurant"
                  >
                    Not this one?
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-[#FFF4F0] rounded-lg p-3 border border-[#A94438]/20" data-testid="restaurant-not-found">
                <div className="flex items-center gap-2 text-sm text-[#A94438] mb-2">
                  <Warning weight="fill" className="w-4 h-4" />
                  <span className="font-medium">
                    {createNewRestaurant ? 'Creating new restaurant' : 'Restaurant not in your list'}
                  </span>
                </div>
                <p className="text-xs text-[#7A7873] ml-6 mb-2">
                  We'll add "{restaurantName}" to your list as "Visited"
                </p>
                {matchedRestaurant && (
                  <button
                    onClick={() => setCreateNewRestaurant(false)}
                    className="text-xs text-[#4B5945] underline ml-6"
                  >
                    Use matched restaurant instead
                  </button>
                )}
              </div>
            )}

            {/* Editable fields */}
            <div>
              <Label>Restaurant Name</Label>
              <Input
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                className="mt-1"
                data-testid="scan-restaurant-name"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={billDate}
                  onChange={(e) => setBillDate(e.target.value)}
                  className="mt-1"
                  data-testid="scan-bill-date"
                />
              </div>
              <div>
                <Label>Total (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={totalBill}
                  onChange={(e) => setTotalBill(e.target.value)}
                  className="mt-1"
                  data-testid="scan-total-bill"
                />
              </div>
              <div>
                <Label>Tip (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="mt-1"
                  data-testid="scan-tip-amount"
                />
              </div>
            </div>

            {/* Line items (toggleable) */}
            {scanResult.items?.length > 0 && (
              <div>
                <button
                  onClick={() => setShowItems(!showItems)}
                  className="flex items-center gap-2 text-sm font-medium text-[#5A5752] hover:text-[#1A1918] transition-colors w-full"
                  data-testid="toggle-items-btn"
                >
                  {showItems ? <CaretUp weight="bold" className="w-4 h-4" /> : <CaretDown weight="bold" className="w-4 h-4" />}
                  {scanResult.items.length} items extracted
                </button>

                {showItems && (
                  <div className="mt-2 space-y-1 max-h-[200px] overflow-y-auto" data-testid="line-items-list">
                    {scanResult.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-sm py-1.5 px-3 bg-[#F9F8F5] rounded"
                      >
                        <span className="text-[#5A5752]">
                          {item.qty > 1 && <span className="text-[#9A9590] mr-1">{item.qty}x</span>}
                          {item.name}
                        </span>
                        <span className="font-medium text-[#1A1918]">
                          R{typeof item.price === 'number' ? item.price.toFixed(2) : item.price}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Save */}
            <Button
              onClick={handleSaveVisit}
              disabled={!restaurantName.trim()}
              className="w-full btn-primary"
              data-testid="save-scanned-visit-btn"
            >
              <Check weight="bold" className="w-5 h-5 mr-2" />
              Log Visit from Bill
            </Button>
          </div>
        )}

        {/* Step 4: Saving */}
        {step === 'saving' && (
          <div className="flex flex-col items-center py-12 space-y-4">
            <SpinnerGap className="w-12 h-12 text-[#A94438] animate-spin" />
            <p className="text-lg font-heading font-semibold text-[#1A1918]">Saving visit...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScanBillModal;
